import * as Domain from "@/domain";
import { config } from "@/helpers";
import { RootStore } from "@/store/root";
import format from "date-fns/format";
import parse from "date-fns/parse";
import { makeAutoObservable } from "mobx";
import { IParsedValue } from "./types";

export class ParametersStore {
  private _rootStore: RootStore;
  public loading = false;
  public refreshing = false;
  private _prefix = `@yoney/${config.environment}/parameters`;
  private _parameters: { [key: string]: string | number | boolean | Date } = {};

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;
    makeAutoObservable(this);
  }

  public clear = async (): Promise<void> => {
    this._parameters = {};
    localStorage.clear();
  };

  public load = async (): Promise<void> => {
    this._rootStore.logging.track("parameters.load");

    const storageKeys = Object.keys(localStorage);

    await Promise.all(
      // eslint-disable-next-line
      storageKeys.map((name: string) => {
        if (!name.startsWith(this._prefix)) {
          // eslint-disable-next-line
          return;
        }

        const value = localStorage.getItem(name);
        if (!value) {
          // eslint-disable-next-line
          return;
        }

        try {
          const parsedName = name.replace(`${this._prefix}/`, "");
          const parsedValue = JSON.parse(value) as IParsedValue;
          switch (parsedValue.type) {
            case "string":
              this._parameters[parsedName] = parsedValue.value;

              break;
            case "number":
              this._parameters[parsedName] = parseInt(parsedValue.value, 10);

              break;
            case "boolean":
              this._parameters[parsedName] = "true" === parsedValue.value;

              break;
            case "datetime":
              this._parameters[parsedName] = parse(
                parsedValue.value,
                "yyyy-MM-dd HH:mm",
                new Date()
              );

              if (isNaN(this._parameters[parsedName] as any)) {
                this._parameters[parsedName] = parse(
                  parsedValue.value,
                  "dd.MM.yyyy HH:mm",
                  new Date()
                );
              }

              break;
          }
        } catch (err) {
          this._rootStore.logging.capture(err);
        }
      })
    );
  };

  public writeString = (name: Domain.Parameter.IName, value: string): void => {
    this._parameters[name] = value;
    localStorage.setItem(
      `${this._prefix}/${name}`,
      JSON.stringify({ type: "string", value })
    );
  };

  public writeNumber = (name: Domain.Parameter.IName, value: number): void => {
    this._parameters[name] = value;
    localStorage.setItem(
      `${this._prefix}/${name}`,
      JSON.stringify({ type: "number", value: value.toString() })
    );
  };

  public writeBoolean = (
    name: Domain.Parameter.IName,
    value: boolean
  ): void => {
    this._parameters[name] = value;
    localStorage.setItem(
      `${this._prefix}/${name}`,
      JSON.stringify({ type: "boolean", value: value ? "true" : "false" })
    );
  };

  public writeDateTime = (name: Domain.Parameter.IName, value: Date): void => {
    this._parameters[name] = value;
    localStorage.setItem(
      `${this._prefix}/${name}`,
      JSON.stringify({
        type: "datetime",
        value: format(value, "yyyy-MM-dd HH:mm"),
      })
    );
  };

  public has = (name: Domain.Parameter.IName): boolean =>
    name in this._parameters;

  public read = <T extends string | number | boolean | Date | null>(
    name: Domain.Parameter.IName,
    defaultValue: T
  ): T => (this.has(name) ? (this._parameters[name] as T) : defaultValue);

  public remove = (name: Domain.Parameter.IName): void => {
    delete this._parameters[name];
    localStorage.removeItem(`${this._prefix}/${name}`);
  };
}
